/**
 * Error schema definitions for API error responses
 * Following JSON:API error object specification
 */

import { z } from "zod";

/**
 * An application-specific error code, expressed as a string value.
 */
export const ErrorCodeSchema = z
  .string()
  .describe("An application-specific error code, expressed as a string value.");

/**
 * Identifies the source of the error within the request payload, if relevant.
 */
export const ErrorSourceSchema = z
  .object({
    pointer: z
      .string()
      .optional()
      .describe(
        "A JSON Pointer [RFC6901](https://tools.ietf.org/html/rfc6901) to the associated entity in the request document [e.g. `/data` for a primary data object, or `/data/attributes/title` for a specific attribute].",
      ),
    parameter: z
      .string()
      .optional()
      .describe(
        "A string indicating which URI query parameter caused the error.",
      ),
    header: z
      .string()
      .optional()
      .describe("A string indicating the header that caused the error."),
  })
  .strict()
  .nullable()
  .describe(
    "Identifies the source of the error within the request payload, if relevant.",
  );

/**
 * An error object provides additional information about problems encountered while performing an operation.
 * Error objects MUST be returned as an array keyed by `errors` in the top level of a JSON:API document.
 */
export const ErrorObjectSchema = z
  .object({
    id: z
      .string()
      .nullable()
      .optional()
      .describe(
        "A unique identifier for this particular occurrence of the problem.",
      ),
    links: z
      .object({
        about: z
          .string()
          .url()
          .describe(
            "A link that leads to further details about this particular occurrence of the problem.",
          ),
      })
      .strict()
      .nullable()
      .optional()
      .describe("Relevant links about the error"),
    status: z
      .string()
      .optional()
      .describe(
        "The HTTP status code applicable to this problem, expressed as a string value.",
      ),
    code: ErrorCodeSchema.optional(),
    title: z
      .string()
      .optional()
      .describe(
        "A short, human-readable summary of the problem that SHOULD NOT change from occurrence to occurrence of the problem, except for purposes of localization.",
      ),
    detail: z
      .string()
      .optional()
      .describe(
        "A human-readable explanation specific to this occurrence of the problem. Like `title`, this field's value can be localized.",
      ),
    source: ErrorSourceSchema.optional(),
  })
  .strict()
  .describe(
    "An error object provides additional information about problems encountered while performing an operation. Error objects MUST be returned as an array keyed by `errors` in the top level of a JSON:API document.",
  );

/**
 * A JSON:API error response document containing an array of error objects.
 */
export const ErrorsResponseSchema = z
  .object({
    errors: z
      .array(ErrorObjectSchema)
      .describe("A collection of the errors returned."),
  })
  .describe(
    "A JSON:API error response document containing an array of error objects.",
  );

// Export TypeScript types
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;
export type ErrorSource = z.infer<typeof ErrorSourceSchema>;
export type ErrorObject = z.infer<typeof ErrorObjectSchema>;
export type ErrorsResponse = z.infer<typeof ErrorsResponseSchema>;
